import { Mail, Phone, Send, FileText, MessageSquare } from "lucide-react";
import { formatDate } from "@ckgo2/shared/utils";
import clsx from "clsx";
import type { Communication, Order } from "./types";

interface CustomerCommunicationsProps {
  communications: Communication[];
  onOpenSendModal: (type: "email" | "pdf", order?: Order) => void;
}

export default function CustomerCommunications({
  communications,
  onOpenSendModal,
}: CustomerCommunicationsProps) {
  return (
    <div className="card">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Communications</h2>
        <button onClick={() => onOpenSendModal("email")} className="btn-ghost btn-sm">
          <Send className="mr-2 h-4 w-4" />
          New message
        </button>
      </div>

      {communications.length > 0 ? (
        <div className="space-y-3">
          {communications.map((comm) => {
            const typeConfig: Record<string, { icon: typeof Mail; color: string; label: string }> =
              {
                email: { icon: Mail, color: "text-blue-600", label: "Email" },
                phone: { icon: Phone, color: "text-green-600", label: "Phone" },
                sms: { icon: MessageSquare, color: "text-purple-600", label: "SMS" },
                note: { icon: FileText, color: "text-gray-600", label: "Note" },
              };
            const config = typeConfig[comm.type] || typeConfig.note;
            const Icon = config.icon;

            return (
              <div
                key={comm.id}
                className="flex gap-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700"
              >
                <div className={clsx("mt-1", config.color)}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {comm.subject || config.label}
                      </span>
                      <span
                        className={clsx(
                          "rounded px-2 py-0.5 text-xs",
                          comm.direction === "outbound"
                            ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                            : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
                        )}
                      >
                        {comm.direction === "outbound" ? "Sent" : "Received"}
                      </span>
                      {comm.emailStatus && (
                        <span
                          className={clsx(
                            "rounded px-2 py-0.5 text-xs",
                            comm.emailStatus === "delivered"
                              ? "bg-success-100 text-success-700"
                              : comm.emailStatus === "opened"
                                ? "bg-primary-100 text-primary-700"
                                : "bg-gray-100 text-gray-600",
                          )}
                        >
                          {comm.emailStatus === "delivered"
                            ? "Delivered"
                            : comm.emailStatus === "opened"
                              ? "Opened"
                              : comm.emailStatus}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">{formatDate(comm.createdAt)}</span>
                  </div>
                  {comm.content && (
                    <p className="mt-2 line-clamp-2 text-sm text-gray-600 dark:text-gray-400">
                      {comm.content}
                    </p>
                  )}
                  {comm.createdBy && (
                    <p className="mt-1 text-xs text-gray-500">From: {comm.createdBy}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex h-32 flex-col items-center justify-center text-gray-500">
          <MessageSquare className="mb-2 h-8 w-8 text-gray-300" />
          <p>No communications</p>
        </div>
      )}
    </div>
  );
}
